import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Funnel, Plus, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, PageHeader, ui } from "../../../components/ui";
import { InventoryRecordTable } from "../components/InventoryRecordTable";
import {
  EMPTY_INVENTORY_RECORD_FILTERS,
  InventoryRecordFiltersModal,
} from "../components/InventoryRecordFiltersModal";
import { InventoryRecordDetailModal } from "../components/InventoryRecordDetailModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

const PAGE_SIZE = 10;
const FILTER_LOOKUP_PAGE_SIZE = 50;

function toFilterOptions(rows) {
  return (rows || []).map((row) => ({
    value: String(row.id),
    label: row.name || String(row.id),
  }));
}

function mergeFilterOptions(current, incoming) {
  const seen = new Set(current.map((option) => option.value));
  const merged = [...current];
  incoming.forEach((option) => {
    if (seen.has(option.value)) return;
    seen.add(option.value);
    merged.push(option);
  });
  return merged;
}

function countActiveFilters(filters) {
  let count = 0;
  if (filters.sourceType && filters.sourceType !== "All") count += 1;
  if (String(filters.batchNumber || "").trim()) count += 1;
  if (filters.productId && filters.productId !== "All") count += 1;
  if (filters.warehouseLocationId && filters.warehouseLocationId !== "All") {
    count += 1;
  }
  return count;
}

function hasActiveFilters(filters) {
  return countActiveFilters(filters) > 0;
}

export function RecordInventoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ ...EMPTY_INVENTORY_RECORD_FILTERS });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const productFetchLock = useRef(false);
  const productLookupRef = useRef({ page: 0, hasMore: false, loaded: false });

  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingMoreWarehouses, setLoadingMoreWarehouses] = useState(false);
  const warehouseFetchLock = useRef(false);
  const warehouseLookupRef = useRef({ page: 0, hasMore: false, loaded: false });

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  const handleCreate = () => {
    navigate("/inventory-management/record-inventory-in/create");
  };

  const loadProductOptions = useCallback(async (pageNum, { append = false } = {}) => {
    if (productFetchLock.current) return;
    productFetchLock.current = true;
    if (append) setLoadingMoreProducts(true);
    else setLoadingProducts(true);

    try {
      const result = await inventoryManagementService.getInventoryProducts({
        page: pageNum,
        size: FILTER_LOOKUP_PAGE_SIZE,
      });
      const next = toFilterOptions(result.content);
      setProductOptions((prev) => (append ? mergeFilterOptions(prev, next) : next));
      productLookupRef.current = {
        page: result.page ?? pageNum,
        hasMore: Boolean(result.hasMore),
        loaded: true,
      };
    } catch (err) {
      console.error("Failed to load product filter lookups:", err);
      if (!append) {
        setProductOptions([]);
        productLookupRef.current = { page: 0, hasMore: false, loaded: false };
      }
    } finally {
      setLoadingProducts(false);
      setLoadingMoreProducts(false);
      productFetchLock.current = false;
    }
  }, []);

  const loadWarehouseOptions = useCallback(async (pageNum, { append = false } = {}) => {
    if (warehouseFetchLock.current) return;
    warehouseFetchLock.current = true;
    if (append) setLoadingMoreWarehouses(true);
    else setLoadingWarehouses(true);

    try {
      const result = await inventoryManagementService.getWarehouses({
        page: pageNum,
        size: FILTER_LOOKUP_PAGE_SIZE,
      });
      const next = toFilterOptions(result.content);
      setWarehouseOptions((prev) =>
        append ? mergeFilterOptions(prev, next) : next
      );
      warehouseLookupRef.current = {
        page: result.page ?? pageNum,
        hasMore: Boolean(result.hasMore),
        loaded: true,
      };
    } catch (err) {
      console.error("Failed to load warehouse filter lookups:", err);
      if (!append) {
        setWarehouseOptions([]);
        warehouseLookupRef.current = { page: 0, hasMore: false, loaded: false };
      }
    } finally {
      setLoadingWarehouses(false);
      setLoadingMoreWarehouses(false);
      warehouseFetchLock.current = false;
    }
  }, []);

  const handleFilterModalOpenChange = useCallback(
    (open) => {
      setFilterModalOpen(open);
      if (!open) return;
      // Product / warehouse lookups only when Filter is opened — not on page load
      if (!productLookupRef.current.loaded && !productFetchLock.current) {
        loadProductOptions(0);
      }
      if (!warehouseLookupRef.current.loaded && !warehouseFetchLock.current) {
        loadWarehouseOptions(0);
      }
    },
    [loadProductOptions, loadWarehouseOptions]
  );

  const handleProductDropdownOpen = useCallback(
    (open) => {
      if (!open || productLookupRef.current.loaded || productFetchLock.current) {
        return;
      }
      loadProductOptions(0);
    },
    [loadProductOptions]
  );

  const handleWarehouseDropdownOpen = useCallback(
    (open) => {
      if (
        !open ||
        warehouseLookupRef.current.loaded ||
        warehouseFetchLock.current
      ) {
        return;
      }
      loadWarehouseOptions(0);
    },
    [loadWarehouseOptions]
  );

  const handleProductListScroll = useCallback(
    (event) => {
      if (!productLookupRef.current.hasMore || productFetchLock.current) return;
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
        loadProductOptions(productLookupRef.current.page + 1, { append: true });
      }
    },
    [loadProductOptions]
  );

  const handleWarehouseListScroll = useCallback(
    (event) => {
      if (!warehouseLookupRef.current.hasMore || warehouseFetchLock.current) {
        return;
      }
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
        loadWarehouseOptions(warehouseLookupRef.current.page + 1, {
          append: true,
        });
      }
    },
    [loadWarehouseOptions]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    const fetchPromise = hasActiveFilters(filters)
      ? inventoryManagementService.searchInventoryRecords({
          ...filters,
          page: page - 1,
          size: PAGE_SIZE,
        })
      : inventoryManagementService.getInventoryRecords({
          page: page - 1,
          size: PAGE_SIZE,
          sortBy: "inventoryInId",
          sortDir: "DESC",
        });

    fetchPromise
      .then(async (res) => {
        if (!active) return;
        const content = res.content || [];
        const withCustomerNames =
          await inventoryManagementService.enrichInventoryRecordCustomerNames(
            content
          );
        if (!active) return;
        setRows(withCustomerNames);
        setTotalItems(res.totalElements ?? withCustomerNames.length);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load inventory records:", err);
        setRows([]);
        setTotalItems(0);
        setError(t("validation.failedToRetrieveInventoryRecords"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [page, filters, t]);

  const handleApplyFilters = (nextFilters) => {
    setPage(1);
    setFilters({
      sourceType: nextFilters.sourceType || "All",
      batchNumber: String(nextFilters.batchNumber || "").trim(),
      productId: nextFilters.productId || "All",
      warehouseLocationId: nextFilters.warehouseLocationId || "All",
    });
  };

  const handleResetFilters = () => {
    setPage(1);
    setFilters({ ...EMPTY_INVENTORY_RECORD_FILTERS });
  };

  return (
    <AppLayout hideBreadcrumb className="space-y-6">
      <PageHeader
        back={
          <PageBackLink
            to="/inventory-management/stock-position"
            label={t("formActions.back")}
          />
        }
        title={t("inventory.recordInventoryIn")}
        action={
          <button
            type="button"
            onClick={handleCreate}
            className={ui.primaryBtn}
          >
            <Plus className="h-4 w-4" />
            <span>{t("formActions.create")}</span>
          </button>
        }
      />
      <ContentCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
          <span className="text-xs font-bold text-foreground">
            {t("inventory.inventoryRecordsOverview")}
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleFilterModalOpenChange(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-workspace/40 hover:text-primary transition cursor-pointer"
            >
              <Funnel className="h-3.5 w-3.5" />
              <span>{t("formActions.filter")}</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleResetFilters}
              disabled={activeFilterCount === 0}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              <RefreshCw className="h-3.5 w-3.5 per-file-hint" />
              <span>{t("formActions.resetFilters")}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-10 bg-gray-100 rounded-lg w-full" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((idx) => (
                <div
                  key={idx}
                  className="h-12 bg-gray-50/50 rounded-lg w-full flex items-center gap-4 px-4"
                >
                  <div className="h-4 bg-gray-100 rounded w-20" />
                  <div className="h-4 bg-gray-100 rounded w-24" />
                  <div className="h-4 bg-gray-100 rounded w-1/4" />
                  <div className="h-4 bg-gray-100 rounded w-12" />
                  <div className="h-4 bg-gray-100 rounded w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-error font-semibold text-sm">
            ⚠️ {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center font-sans">
            <div className="h-12 w-12 rounded-full bg-workspace per-file-hint grid place-items-center mb-4">
              <span>📦</span>
            </div>
            <h4 className="text-sm font-bold text-foreground">
              {t("inventory.noInventoryRecordsFound")}
            </h4>
            <p className="text-xs per-file-hint mt-1.5 leading-relaxed max-w-sm">
              {t("inventory.noInventoryRecordsHint")}
            </p>
          </div>
        ) : (
          <InventoryRecordTable
            data={rows}
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={setPage}
            onRowClick={(row) =>
              setDetailId(row.inventoryInId || row.id || null)
            }
          />
        )}
      </ContentCard>

      <InventoryRecordFiltersModal
        open={filterModalOpen}
        onOpenChange={handleFilterModalOpenChange}
        appliedFilters={filters}
        onApply={handleApplyFilters}
        productOptions={productOptions}
        warehouseOptions={warehouseOptions}
        loadingProducts={loadingProducts}
        loadingMoreProducts={loadingMoreProducts}
        loadingWarehouses={loadingWarehouses}
        loadingMoreWarehouses={loadingMoreWarehouses}
        onProductDropdownOpen={handleProductDropdownOpen}
        onWarehouseDropdownOpen={handleWarehouseDropdownOpen}
        onProductListScroll={handleProductListScroll}
        onWarehouseListScroll={handleWarehouseListScroll}
      />

      <InventoryRecordDetailModal
        open={detailId != null}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
        inventoryInId={detailId}
      />
    </AppLayout>
  );
}
